import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectKeyDataContact, selectKeyDataPendingOrders, setKeyDataPendingOrders } from '../../../redux/features/keyDataSlice'
import OrderDetail from './OrderDetail'
import EditOrder from '../EditOrder'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm'
import useManageOrder from '../../order/useManageOrder'
import OrderService from '../../order/OrderService'
import { toast } from 'react-toastify'
import MassEditSmileName from './MassEditSmileName'

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // This sets the overlay color
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '55%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: 'auto',
    marginRight: '0%',
    transform: 'translate(-50%, -50%)',
  },
};

const OrderDetailList = ({orderGroup, refreshPendingOrder, onClose}) => {

  const {updateOrder} = useManageOrder();
  const profileDetail = useSelector(selectKeyDataPendingOrders)
  const contact = useSelector(selectKeyDataContact)
  const dispatch = useDispatch()
  const [editingOrder, setEditingOrder] = useState({})
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSelectMode, setIsSelectMode] = useState(false); // New state for selection mode
  const [selectedOrders, setSelectedOrders] = useState([]); // New state for selected orders
  const [selectAll, setSelectAll] = useState(false);
  // Pristine copy of the order as loaded, to detect a stale form before saving.
  const originalRef = useRef(null);

  // Payment/status fields the payment webhook owns. We only block a save when one of
  // THESE changed server-side since the form loaded — not on any updatedAt bump, which
  // fires constantly from benign background writes and would block every normal edit.
  const PROTECTED_FIELDS = [
    'productPayment', 'transactionFeePaid', 'shipJpPaid',
    'weightPaid', 'shipThPaid', 'extraJpFeesPaid', 'orderStatus',
  ];
  const protectedFieldChanged = (baseline, fresh) =>
    PROTECTED_FIELDS.some(f => String(fresh?.[f] ?? '') !== String(baseline?.[f] ?? ''));

  // Snapshot the order at selection time so edits mutate editingOrder, not the baseline.
  const selectOrder = (order) => {
    originalRef.current = order;
    setEditingOrder(order);
  };

  // Re-read the order from the server and check whether a payment/status field we must
  // not overwrite changed since the form loaded. Returns true when still safe to save.
  const isStillFresh = async () => {
    const baseline = originalRef.current;
    if (!baseline?.uuid || !baseline?.shop) return true; // nothing loaded → nothing to protect
    try {
      const res = await OrderService.getOrderByUuid(baseline.uuid, baseline.shop);
      return !protectedFieldChanged(baseline, res.data);
    } catch (err) {
      // Fail open. getOrderByUuid returns the row for ANY existing order (no status
      // filter), so a real change never 404s — a 404/error here means the endpoint is
      // unreachable or params are bad, which must never block a legitimate save.
      return true;
    }
  };

  // Save gate used by every writer here: block + warn when the form is stale.
  const ensureFresh = async () => {
    const fresh = await isStillFresh();
    if (!fresh) {
      toast.warning('ออเดอร์นี้เพิ่งถูกอัปเดต กรุณาโหลดใหม่ก่อนบันทึก');
      return false;
    }
    return true;
  };

  // After our own successful save, re-read the order into the baseline so the next
  // edit compares against current values (and never against our own change).
  const syncBaseline = async () => {
    const baseline = originalRef.current;
    if (!baseline?.uuid || !baseline?.shop) return;
    try {
      const res = await OrderService.getOrderByUuid(baseline.uuid, baseline.shop);
      originalRef.current = res.data;
    } catch (err) {
      // Order left the pending set — nothing more to edit here; leave baseline as-is.
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectAll(false);
    setSelectedOrders([]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked && profileDetail?.orders) {
      setSelectedOrders(profileDetail.orders);
      setSelectAll(true);
    } else {
      setSelectedOrders([]);
      setSelectAll(false);
    }
  };

  const openConfirm = async () => { 
    setIsConfirmOpen(true)
  };

  const closeConfirm = () =>{
    setIsConfirmOpen(false)
  }

  const handleArchive = async () => {
    if (!(await ensureFresh())) return;
    // Only the field we intend to change — never the whole (possibly stale) order.
    const updatedOrder = {
      uuid: editingOrder.uuid,
      id: editingOrder.id,
      orderStatus: 'hide'
    };
    const wasLastOrder = profileDetail?.orders?.length === 1;
    const response = await updateOrder(updatedOrder);
    if (response) {
      await refreshPendingOrder();
      toast.success("Success");
      closeConfirm();
      if (wasLastOrder && onClose) {
        onClose();
      }
    }
  }

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
    setSelectAll(profileDetail?.orders && selectedOrders.length + 1 === profileDetail.orders.length);
  };

  const handleMassUpdate = async (newSmileName) => {
    console.log(selectedOrders)
    const promises = selectedOrders.map((order) =>
      updateOrder({ uuid: order.uuid, id: order.id, smileNameInput: newSmileName })
    );
    await Promise.all(promises);
    await refreshPendingOrder();
    toast.success('Smile names updated successfully');
    setIsSelectMode(false);
    setSelectedOrders([]);
  };

  useEffect(() => {
    const filterOrderGroup = orderGroup.find(obj => obj.contact === contact);
    // Only sync when we have a match; don't overwrite with undefined when orderGroup is empty or contact not in list
    if (filterOrderGroup != null) {
      dispatch(setKeyDataPendingOrders(filterOrderGroup));
    }
  }, [orderGroup, contact]);

  useEffect(() => {
    const orders = profileDetail?.orders ?? [];
    setSelectAll(selectedOrders.length === orders.length && orders.length > 0);
  }, [selectedOrders, profileDetail?.orders]);

  return (
    <div>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm
          data={editingOrder}
          closeConfirm={closeConfirm}
          confirmFunction={handleArchive}
          titleText={"Are you confirm to hide ?"}
        />
      </Modal>
      <div className="border-b">
        {profileDetail && <div>{profileDetail.contact}</div>}
        {isSelectMode && (
          <div>
            <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
            <label >Select All</label>
          </div>
        )}
      </div>
      <div className="grid grid-cols-5 gap-x-5 mt-4" style={{ height: '800px' }}>
        <div className="col-span-3 overflow-y-auto h-4/5">
          {profileDetail?.orders?.length ? (
            profileDetail.orders.map((order, index) => (
              <OrderDetail
                key={index}
                order={order}
                index={index}
                setOrder={selectOrder}
                refreshPendingOrder={refreshPendingOrder}
                openConfirm={openConfirm}
                isSelectMode={isSelectMode}
                handleSelectOrder={handleSelectOrder}
                selectedOrders={selectedOrders}
              />
            ))
          ) : (
            <div className="p-4 text-gray-500">
              {profileDetail == null ? 'Select a customer from the list to view orders.' : 'No orders for this customer.'}
            </div>
          )}
        </div>
        <div className="col-span-2 h-4/5">
          {isSelectMode && (
            <MassEditSmileName
              handleUpdate={handleMassUpdate}
              selectedOrders={selectedOrders}
            />
          )}
          <button onClick={toggleSelectMode} className="btn-primary mt-2 ml-3">
            {isSelectMode ? 'Exit Select Mode' : 'Mass Edit Smile Name'}
          </button>
          <EditOrder
            editingOrder={editingOrder}
            setEditingOrder={setEditingOrder}
            refreshPendingOrder={refreshPendingOrder}
            onBeforeSave={ensureFresh}
            onSaved={syncBaseline}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailList